import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export const TOUR_KEY = 'demo_tour_done';

const STEPS = [
    { target: '[data-tour="this-week"]', title: 'This Week', body: "Every member sees the group's current lesson here: watch, read, reflect, discuss and attend." },
    { target: '[data-tour="courses"]', title: 'Courses', body: 'Leaders build courses with lessons, resources and discussion questions. Open one to look inside.' },
    { target: '[data-testid="nav-connect"], [data-testid="bottom-nav-connect"]', title: 'Connect', body: 'Group chat and private messages with your leader keep conversations going between meetings.' },
    { target: '[data-testid="nav-my progress"], [data-testid="bottom-nav-my progress"]', title: 'My Progress', body: 'Streaks and course completion help members stay on track week after week.' },
    { target: '[data-tour="begin"]', title: "Try this week's lesson", body: 'Open the lesson to see the video, a sample handout and the group discussion in action.', cta: true },
];

const findVisible = (selector) => [...document.querySelectorAll(selector)].find((el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
});

const useTargetRect = (selector, step) => {
    const [rect, setRect] = useState(null);
    useLayoutEffect(() => {
        const el = findVisible(selector);
        if (!el) { setRect(null); return undefined; }
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const update = () => setRect(el.getBoundingClientRect());
        const t = setTimeout(update, 350);
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => { clearTimeout(t); window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true); };
    }, [selector, step]);
    return rect;
};

const tooltipStyle = (rect) => {
    const width = Math.min(340, window.innerWidth - 24);
    if (!rect) return { width, left: (window.innerWidth - width) / 2, top: window.innerHeight / 2 - 90 };
    const left = Math.min(Math.max(12, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 12);
    if (rect.bottom + 200 < window.innerHeight) return { width, left, top: rect.bottom + 14 };
    if (rect.top > 214) return { width, left, bottom: window.innerHeight - rect.top + 14 };
    return { width, left, bottom: 96 };
};

export const GuestTour = ({ open, onClose, onOpenLesson }) => {
    const [step, setStep] = useState(0);
    const current = STEPS[step];
    const rect = useTargetRect(current.target, step);

    const finish = useCallback(() => {
        localStorage.setItem(TOUR_KEY, '1');
        setStep(0);
        onClose();
    }, [onClose]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') finish(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [finish]);

    if (!open) return null;
    const pad = 8;
    const openLesson = () => { finish(); onOpenLesson(); };

    return (
        <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label="Demo tour" data-testid="guest-tour">
            <div
                className="absolute rounded-2xl transition-all duration-300 pointer-events-none ring-2 ring-white/80"
                style={rect
                    ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, boxShadow: '0 0 0 9999px rgba(15,20,12,0.62)' }
                    : { inset: 0, boxShadow: '0 0 0 9999px rgba(15,20,12,0.62)' }}
                data-testid="guest-tour-spotlight"
            />
            <div className="absolute bg-card text-card-foreground rounded-2xl shadow-2xl p-5 space-y-3 animate-fade-in" style={tooltipStyle(rect)} data-testid="guest-tour-tooltip">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="text-[11px] uppercase tracking-widest font-semibold text-primary">Step {step + 1} of {STEPS.length}</p>
                        <h3 className="text-lg font-bold" style={{ fontFamily: "'Fraunces', serif" }} data-testid="guest-tour-title">{current.title}</h3>
                    </div>
                    <button onClick={finish} aria-label="Close tour" className="p-1 rounded-full hover:bg-muted" data-testid="guest-tour-close"><X className="w-4 h-4" /></button>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{current.body}</p>
                <div className="flex items-center justify-between pt-1">
                    <button onClick={finish} className="text-sm text-muted-foreground hover:text-foreground" data-testid="guest-tour-skip">Skip tour</button>
                    <div className="flex gap-2">
                        {step > 0 && <Button size="sm" variant="outline" onClick={() => setStep(step - 1)} data-testid="guest-tour-back">Back</Button>}
                        {current.cta ? (
                            <Button size="sm" onClick={openLesson} data-testid="guest-tour-open-lesson">Open lesson <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
                        ) : (
                            <Button size="sm" onClick={() => setStep(step + 1)} data-testid="guest-tour-next">Next <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GuestTour;
